import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Col, Row } from 'reactstrap';

import Select from './Select';
import { optionsPropTypes } from './SelectPropTypes';

import {
  clearMaintenanceActivtySystems,
  getMaintenanceActivtySystems,
} from '../../../../actions/mechanical-maintenance/MaintenanceActivitySystem';

export const MaintenanceActivitySystemSelect = ({
  loading,
  options,
  systemId,
  subsystemId,
  dispatchGetMaintenanceActivitySystems,
  dispatchClearMaintenanceActivitySystems,
  isGridVariant,
  isClearable,
}) => {
  useEffect(() => {
    dispatchGetMaintenanceActivitySystems({
      page: 0,
      size: 100,
      sort: 'name,asc',
    });

    return () => {
      dispatchClearMaintenanceActivitySystems();
    };
  }, [
    dispatchGetMaintenanceActivitySystems,
    dispatchClearMaintenanceActivitySystems,
  ]);

  /*
   * Valor actual guardado en redux-form.
   *
   * Puede venir como:
   * 1
   *
   * O como:
   * {
   *   value: 1,
   *   label: 'Motor'
   * }
   */
  const systemValue = systemId.input.value;

  /*
   * Obtiene el objeto completo de la opción seleccionada.
   * Esto permite que el Select muestre correctamente el label.
   */
  const selectedSystemOption = useMemo(() => {
    if (!systemValue) {
      return null;
    }

    const selectedSystemId =
      typeof systemValue === 'object' ? systemValue.value : systemValue;

    const selectedOption = options.find(
      (option) => Number(option.value) === Number(selectedSystemId),
    );

    /*
     * Si todavía no cargaron las opciones, conserva el objeto
     * que ya estaba guardado en redux-form para mostrar su label.
     */
    if (!selectedOption && typeof systemValue === 'object') {
      return systemValue;
    }

    return selectedOption || null;
  }, [options, systemValue]);

  /*
   * Los subsistemas ya vienen dentro del sistema.
   * No es necesario realizar otra petición al backend.
   */
  const subsystemOptions = useMemo(() => {
    if (!selectedSystemOption) {
      return [];
    }

    return selectedSystemOption.subsystemOptions || [];
  }, [selectedSystemOption]);

  const subsystemValue = subsystemId.input.value;

  /*
   * Obtiene la opción completa del subsistema seleccionado.
   */
  const selectedSubsystemOption = useMemo(() => {
    if (!subsystemValue) {
      return null;
    }

    const selectedSubsystemId =
      typeof subsystemValue === 'object'
        ? subsystemValue.value
        : subsystemValue;

    const selectedOption = subsystemOptions.find(
      (option) => Number(option.value) === Number(selectedSubsystemId),
    );

    if (!selectedOption && typeof subsystemValue === 'object') {
      return subsystemValue;
    }

    return selectedOption || null;
  }, [subsystemOptions, subsystemValue]);

  /*
   * Al cambiar de sistema se guarda el option completo
   * y se limpia el subsistema anterior.
   */
  const handleSystemChange = (option) => {
    systemId.input.onChange(option);
    subsystemId.input.onChange(null);
  };

  /*
   * Guarda el option completo del subsistema.
   */
  const handleSubsystemChange = (option) => {
    subsystemId.input.onChange(option);
  };

  return (
    <Row>
      <Col lg={6} md={6} sm={12}>
        <Select
          input={{
            ...systemId.input,
            value: selectedSystemOption,
            onChange: handleSystemChange,
          }}
          meta={systemId.meta}
          options={options}
          isLoading={loading}
          isClearable={isClearable}
          isGridVariant={isGridVariant}
          placeholder="Selecciona un sistema"
        />
      </Col>

      <Col lg={6} md={6} sm={12}>
        <Select
          input={{
            ...subsystemId.input,
            value: selectedSubsystemOption,
            onChange: handleSubsystemChange,
          }}
          meta={subsystemId.meta}
          options={subsystemOptions}
          isLoading={loading}
          isClearable={isClearable}
          isGridVariant={isGridVariant}
          isDisabled={!selectedSystemOption}
          placeholder={
            selectedSystemOption
              ? 'Selecciona un subsistema'
              : 'Primero selecciona un sistema'
          }
        />
      </Col>
    </Row>
  );
};

const optionPropType = PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  label: PropTypes.string,
});

const fieldPropType = PropTypes.shape({
  input: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
      optionPropType,
    ]),
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
  }).isRequired,

  meta: PropTypes.shape({
    active: PropTypes.bool,
    dirty: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    invalid: PropTypes.bool,
    pristine: PropTypes.bool,
    submitFailed: PropTypes.bool,
    touched: PropTypes.bool,
    valid: PropTypes.bool,
    visited: PropTypes.bool,
  }),
});

MaintenanceActivitySystemSelect.propTypes = {
  loading: PropTypes.bool.isRequired,
  options: optionsPropTypes,

  systemId: fieldPropType.isRequired,
  subsystemId: fieldPropType.isRequired,

  dispatchGetMaintenanceActivitySystems: PropTypes.func.isRequired,

  dispatchClearMaintenanceActivitySystems: PropTypes.func.isRequired,

  isGridVariant: PropTypes.bool,
  isClearable: PropTypes.bool,
};

MaintenanceActivitySystemSelect.defaultProps = {
  options: [],
  isGridVariant: false,
  isClearable: true,
};

/*
 * Convierte valores Immutable a JavaScript.
 */
const toPlainJavaScript = (value) => {
  if (!value) {
    return value;
  }

  return typeof value.toJS === 'function' ? value.toJS() : value;
};

/*
 * Convierte los subsistemas al formato utilizado por Select.
 */
const generateSubsystemOptions = (subsystemList = []) => {
  const plainSubsystemList = toPlainJavaScript(subsystemList) || [];

  return plainSubsystemList.map((subsystem) => ({
    value: subsystem.id,
    label: subsystem.name,
    systemId: subsystem.systemId,
  }));
};

/*
 * Convierte los sistemas al formato utilizado por Select.
 *
 * Cada opción de sistema conserva su listado de subsistemas.
 */
const generateSystemOptions = (systemList = []) => {
  const plainSystemList = toPlainJavaScript(systemList) || [];

  return plainSystemList.map((system) => ({
    value: system.id,
    label: system.name,
    subsystemOptions: generateSubsystemOptions(system.subsystemList),
  }));
};

const mapStateToProps = ({ MechanicalMaintenanceUnit }) => {
  const systemState = MechanicalMaintenanceUnit.MaintenanceActivitySystem;

  const rawSystems = systemState.getIn(['all', 'content', 'content']) || [];

  return {
    options: generateSystemOptions(rawSystems),

    loading: systemState.getIn(['all', 'loading']) || false,
  };
};

const mapDispatchToProps = {
  dispatchGetMaintenanceActivitySystems: getMaintenanceActivtySystems,

  dispatchClearMaintenanceActivitySystems: clearMaintenanceActivtySystems,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(MaintenanceActivitySystemSelect);
